import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FiMapPin, FiAlertTriangle, FiInfo, FiCheckSquare, FiSquare, FiMail } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  getDerasarPersons,
  initiatePersonTransfer,
  getOutgoingPersonTransfers,
  getIncomingPersonTransfers,
  respondToPersonTransfer,
  cancelPersonTransfer,
} from '../../services/api';
import styles from './PersonsMigrationPage.module.css';

const fmtDate = (str) => {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatAddress = (entry) => {
  if (!entry) return null;
  return [
    entry.flatNo && entry.buildingName
      ? `${entry.flatNo}, ${entry.buildingName}`
      : (entry.flatNo || entry.buildingName),
    entry.addressLine1, entry.addressLine2,
    entry.city, entry.state,
    entry.pincode,
  ].filter(Boolean).join(', ') || null;
};

const StatusBadge = ({ status }) => (
  <span className={`${styles.statusBadge} ${styles[status] || ''}`}>{status}</span>
);

const PersonsMigrationPage = () => {
  const { activeDerasarId } = useAuth();

  const [persons, setPersons]         = useState([]);
  const [selectedIds, setSelectedIds]  = useState([]);
  const [toEmail, setToEmail]          = useState('');
  const [note, setNote]                = useState('');
  const [sending, setSending]          = useState(false);

  const [outgoing, setOutgoing]        = useState([]);
  const [incoming, setIncoming]        = useState([]);
  const [responding, setResponding]    = useState(null);
  const [cancelling, setCancelling]    = useState(null);

  const loadPersons = useCallback(async () => {
    if (!activeDerasarId) return;
    try {
      const res = await getDerasarPersons(activeDerasarId);
      setPersons(res.data?.data || []);
    } catch {
      toast.error('Failed to load your persons');
    }
  }, [activeDerasarId]);

  const loadTransfers = useCallback(async () => {
    if (!activeDerasarId) return;
    try {
      const [outRes, inRes] = await Promise.all([
        getOutgoingPersonTransfers(activeDerasarId),
        getIncomingPersonTransfers(activeDerasarId),
      ]);
      setOutgoing(outRes.data?.data || []);
      setIncoming(inRes.data?.data || []);
    } catch {
      toast.error('Failed to load transfer requests');
    }
  }, [activeDerasarId]);

  useEffect(() => {
    loadPersons();
    loadTransfers();
  }, [loadPersons, loadTransfers]);

  // Group persons by address book entry
  const groupedByAddress = useMemo(() => {
    const groups = {};
    for (const p of persons) {
      const key = p.addressBookEntry?.id ? `ab:${p.addressBookEntry.id}` : '__none__';
      if (!groups[key]) {
        groups[key] = { key, label: p.addressBookEntry?.label || null, address: formatAddress(p.addressBookEntry), persons: [] };
      }
      groups[key].persons.push(p);
    }
    return Object.values(groups).sort((a, b) => {
      if (!a.address && b.address) return 1;
      if (a.address && !b.address) return -1;
      return (a.address || '').localeCompare(b.address || '');
    });
  }, [persons]);

  // Address coverage of current selection
  const selectionInsights = useMemo(() => {
    const selectedSet = new Set(selectedIds.map(String));
    const coveredGroups = groupedByAddress
      .map((g) => ({ ...g, selected: g.persons.filter((p) => selectedSet.has(String(p.id))) }))
      .filter((g) => g.selected.length > 0);
    return { coveredGroups, totalSelected: selectedIds.length };
  }, [selectedIds, groupedByAddress]);

  const multiAddress = selectionInsights.coveredGroups.length > 1;

  const isSelected = (id) => selectedIds.some((s) => String(s) === String(id));

  const togglePerson = (id) =>
    setSelectedIds((prev) =>
      prev.some((s) => String(s) === String(id))
        ? prev.filter((s) => String(s) !== String(id))
        : [...prev, id]
    );

  const toggleGroup = (group) => {
    const ids    = group.persons.map((p) => p.id);
    const idStrs = new Set(ids.map(String));
    const all    = ids.every((id) => selectedIds.some((s) => String(s) === String(id)));
    setSelectedIds((prev) =>
      all ? prev.filter((s) => !idStrs.has(String(s))) : [...new Set([...prev, ...ids])]
    );
  };

  const handleSend = async () => {
    if (!toEmail.trim()) { toast.error('Please enter the recipient email'); return; }
    if (selectedIds.length === 0) { toast.error('Select at least one person'); return; }
    setSending(true);
    try {
      await initiatePersonTransfer({
        derasarId: activeDerasarId, toMemberEmail: toEmail.trim(),
        personIds: selectedIds, note: note.trim() || undefined,
      });
      toast.success('Transfer request sent');
      setSelectedIds([]); setToEmail(''); setNote('');
      loadTransfers();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send transfer request');
    } finally { setSending(false); }
  };

  const handleRespond = async (id, action) => {
    setResponding(id + action);
    try {
      await respondToPersonTransfer(id, action);
      toast.success(action === 'accept' ? 'Transfer accepted' : 'Transfer declined');
      loadTransfers();
      if (action === 'accept') loadPersons();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to respond');
    } finally { setResponding(null); }
  };

  const handleCancel = async (id) => {
    setCancelling(id);
    try {
      await cancelPersonTransfer(id);
      toast.info('Transfer request cancelled');
      loadTransfers();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to cancel');
    } finally { setCancelling(null); }
  };

  return (
    <div className={styles.page}>

      {/* ── Contact notice ──────────────────────────────────────────────── */}
      <div className={styles.contactNotice}>
        <FiMail size={15} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          <strong>Not sure how to proceed?</strong> Please contact{' '}
          <strong>Jainam Seva Setu</strong> before doing any migration.
          Our team will guide you to ensure your persons and their records are transferred correctly.
        </span>
      </div>

      {/* ── Send Transfer Request ────────────────────────────────────────── */}
      <div className={styles.panel}>
        <div className={styles.panelTitleRow}>
          <h3 className={styles.panelTitle}>Send Transfer Request</h3>
          <span className={styles.panelSubtitle}>
            Persons shared in offerings &amp; receipts must be migrated together
          </span>
        </div>

        <div className={styles.sendLayout}>

          {/* LEFT — address-grouped person table */}
          <div className={styles.selectorCol}>
            <div className={styles.selectorLabel}>
              Select persons to transfer
              {selectedIds.length > 0 && (
                <span className={styles.selCount}>{selectedIds.length} selected</span>
              )}
            </div>

            {persons.length === 0 ? (
              <p className={styles.emptyState}>You have no persons to transfer.</p>
            ) : (
              <div className={styles.addressGroups}>
                {groupedByAddress.map((group) => {
                  const allSel  = group.persons.every((p) => isSelected(p.id));
                  const someSel = !allSel && group.persons.some((p) => isSelected(p.id));
                  return (
                    <div key={group.key} className={styles.addressGroup}>
                      <div className={styles.addressGroupHeader} onClick={() => toggleGroup(group)}>
                        <span className={styles.groupCheckIcon}>
                          {allSel
                            ? <FiCheckSquare size={14} style={{ color: 'var(--color-primary)' }} />
                            : someSel
                              ? <FiCheckSquare size={14} style={{ color: 'var(--color-primary)', opacity: 0.45 }} />
                              : <FiSquare size={14} style={{ color: 'var(--color-text-muted)' }} />}
                        </span>
                        <FiMapPin size={11} style={{ flexShrink: 0, color: 'var(--color-text-muted)' }} />
                        <span className={styles.groupAddressLabel}>
                          {group.label ? <strong>{group.label} — </strong> : null}
                          {group.address || <em>No address on record</em>}
                        </span>
                        <span className={styles.groupCount}>{group.persons.length}</span>
                      </div>

                      <table className={styles.personTable}>
                        <colgroup>
                          <col style={{ width: 36 }} />
                          <col style={{ width: '24%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '24%' }} />
                          <col />
                        </colgroup>
                        <thead>
                          <tr>
                            <th />
                            <th>Name</th>
                            <th>Mobile</th>
                            <th>Email</th>
                            <th>Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.persons.map((p) => {
                            const sel = isSelected(p.id);
                            return (
                              <tr
                                key={p.id}
                                className={`${styles.personRow} ${sel ? styles.personRowSelected : ''}`}
                                onClick={() => togglePerson(p.id)}
                              >
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={sel}
                                    onChange={() => togglePerson(p.id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>
                                <td>
                                  <div className={styles.personName}>{p.name}</div>
                                </td>
                                <td className={styles.personMeta}>{p.phone || <span className={styles.nil}>—</span>}</td>
                                <td className={`${styles.personMeta} ${styles.ellipsis}`}>{p.email || <span className={styles.nil}>—</span>}</td>
                                <td className={`${styles.personMeta} ${styles.ellipsis}`}>
                                  {group.label
                                    ? <span title={group.address || undefined}><strong>{group.label}</strong>{group.address ? ` — ${group.address}` : ''}</span>
                                    : (group.address || <span className={styles.nil}>—</span>)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT — insights + form */}
          <div className={styles.formCol}>

            {/* Address coverage insight */}
            {selectedIds.length > 0 && (
              <div className={`${styles.insightPanel} ${multiAddress ? styles.insightWarning : styles.insightOk}`}>
                <div className={styles.insightHeader}>
                  {multiAddress ? <FiAlertTriangle size={13} /> : <FiInfo size={13} />}
                  <strong>{multiAddress ? 'Multiple addresses' : 'Selection looks good'}</strong>
                  <span className={styles.insightCount}>{selectionInsights.totalSelected} selected</span>
                </div>
                {multiAddress && (
                  <p className={styles.insightAlert}>
                    Persons span <strong>{selectionInsights.coveredGroups.length} addresses</strong>. Verify this is intentional.
                  </p>
                )}
                <div className={styles.insightGroups}>
                  {selectionInsights.coveredGroups.map((g) => (
                    <div key={g.key} className={styles.insightGroup}>
                      <div className={styles.insightGroupAddr}>
                        <FiMapPin size={10} />
                        <span>{g.label ? <><strong>{g.label}</strong> — </> : null}{g.address || <em>No address</em>}</span>
                      </div>
                      <div className={styles.insightPersonList}>
                        {g.selected.map((p) => (
                          <span key={p.id} className={styles.insightPersonTag}>
                            {p.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recipient + note + send */}
            <div className={styles.fieldGroup}>
              <label>Recipient Email *</label>
              <input
                type="email"
                placeholder="member@example.com"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label>Note (optional)</label>
              <textarea
                placeholder="Add a message for the recipient..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <button
              className={styles.btnPrimary}
              onClick={handleSend}
              disabled={sending || selectedIds.length === 0 || !toEmail.trim()}
              style={{ width: '100%' }}
            >
              {sending ? 'Sending…' : `Send Request${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
            </button>
          </div>
        </div>
      </div>

      {/* ── Incoming Requests ───────────────────────────────────────────── */}
      <div className={styles.panel}>
        <div className={styles.panelTitleRow}>
          <h3 className={styles.panelTitle}>
            Incoming Requests
            {incoming.length > 0 && <span className={styles.panelCount}>{incoming.length}</span>}
          </h3>
        </div>
        {incoming.length === 0 ? (
          <p className={styles.emptyState}>No pending transfer requests for you.</p>
        ) : (
          <div className={styles.transferList}>
            {incoming.map((t) => {
              const items = t.items || [];
              return (
                <div key={t.id} className={styles.transferCard}>
                  <div className={styles.transferCardHeader}>
                    <div className={styles.transferParty}>
                      <span className={styles.transferPartyName}>{t.fromMember?.name || '—'}</span>
                      {t.fromMember?.auth?.email && (
                        <span className={styles.transferPartyEmail}>
                          <FiMail size={11} /> {t.fromMember.auth.email}
                        </span>
                      )}
                    </div>
                    <div className={styles.transferHeaderMeta}>
                      <span className={styles.transferCountBadge}>
                        {items.length} person{items.length === 1 ? '' : 's'}
                      </span>
                      <span className={styles.transferDate}>{fmtDate(t.createdAt)}</span>
                    </div>
                  </div>

                  {t.note && (
                    <div className={styles.transferNote}>
                      <FiInfo size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{t.note}</span>
                    </div>
                  )}

                  <table className={styles.transferPersonTable}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Mobile</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.personId}>
                          <td>
                            <div className={styles.personName}>{item.person?.name || item.personId}</div>
                          </td>
                          <td className={styles.personMeta}>{item.person?.phone || <span className={styles.nil}>—</span>}</td>
                          <td className={`${styles.personMeta} ${styles.ellipsis}`}>{item.person?.email || <span className={styles.nil}>—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className={styles.transferCardActions}>
                    <button className={`${styles.btnPrimary} ${styles.btnSm}`} onClick={() => handleRespond(t.id, 'accept')} disabled={responding === t.id + 'accept'}>
                      {responding === t.id + 'accept' ? '…' : 'Accept'}
                    </button>
                    <button className={`${styles.btnDanger} ${styles.btnSm}`} onClick={() => handleRespond(t.id, 'reject')} disabled={responding === t.id + 'reject'}>
                      {responding === t.id + 'reject' ? '…' : 'Reject'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Outgoing Requests ───────────────────────────────────────────── */}
      <div className={styles.panel}>
        <div className={styles.panelTitleRow}>
          <h3 className={styles.panelTitle}>
            Outgoing Requests
            {outgoing.length > 0 && <span className={styles.panelCount}>{outgoing.length}</span>}
          </h3>
        </div>
        {outgoing.length === 0 ? (
          <p className={styles.emptyState}>You have not sent any transfer requests.</p>
        ) : (
          <div className={styles.transferList}>
            {outgoing.map((t) => {
              const items = t.items || [];
              return (
                <div key={t.id} className={styles.transferCard}>
                  <div className={styles.transferCardHeader}>
                    <div className={styles.transferParty}>
                      <span className={styles.transferPartyName}>{t.toMember?.name || '—'}</span>
                      {t.toMember?.auth?.email && (
                        <span className={styles.transferPartyEmail}>
                          <FiMail size={11} /> {t.toMember.auth.email}
                        </span>
                      )}
                    </div>
                    <div className={styles.transferHeaderMeta}>
                      <StatusBadge status={t.status} />
                      <span className={styles.transferCountBadge}>
                        {items.length} person{items.length === 1 ? '' : 's'}
                      </span>
                      <span className={styles.transferDate}>{fmtDate(t.createdAt)}</span>
                    </div>
                  </div>

                  {t.note && (
                    <div className={styles.transferNote}>
                      <FiInfo size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{t.note}</span>
                    </div>
                  )}

                  <table className={styles.transferPersonTable}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Mobile</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.personId}>
                          <td>
                            <div className={styles.personName}>{item.person?.name || item.personId}</div>
                          </td>
                          <td className={styles.personMeta}>{item.person?.phone || <span className={styles.nil}>—</span>}</td>
                          <td className={`${styles.personMeta} ${styles.ellipsis}`}>{item.person?.email || <span className={styles.nil}>—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {t.status === 'pending' && (
                    <div className={styles.transferCardActions}>
                      <button className={`${styles.btnSecondary} ${styles.btnSm}`} onClick={() => handleCancel(t.id)} disabled={cancelling === t.id}>
                        {cancelling === t.id ? '…' : 'Cancel'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
};

export default PersonsMigrationPage;
